import Data from 'data';
import Locale from 'data/locale';

import { Stats, getStats, FollowerStats } from 'logic/stats';

import { getSkillInfo, InfoCalculator } from 'logic/skills';
import { clone } from 'utils';
import { defaultValue } from './item';

const _L = Locale("ui-equipment.js", "ui-skills.js", "skilldata");

function itemAddStat(item, id, value) {
  if (!item.affixes.all[id]) return false;
  const stat = Data.stats[id];
  if (!stat) return false;
  if (stat.secondary && item.state.secondary >= item.affixes.secondary) return false;
  if (!stat.secondary && item.state.primary >= item.affixes.primary) return false;
  if (item.state.groups[stat.group || id]) return false;
  if (stat.secondary) item.state.secondary += 1;
  else item.state.primary += 1;
  item.state.groups[stat.group || id] = true;
  let src = (value || item.affixes.all[id]);
  if (item.imported && item.imported.stats && item.imported.stats[id]) {
    if (item.imported.stats[id].every((val, i) => val >= src[i])) {
      src = item.imported.stats[id].slice();
    }
  }
  if (item.stats[id]) {
    if (stat.dr) {
      for (let i = 0; i < src.length; ++i) item.stats[id][i] = 100 - (100 - (item.stats[id][i] || 0)) * (100 - src[i]) / 100;
    } else {
      for (let i = 0; i < src.length; ++i) item.stats[id][i] = (item.stats[id][i] || 0) + src[i];
    }
  } else {
    item.stats[id] = src;
  }
  return true;
}

function itemCreate(source, options) {
  const item = clone(source, true);
  item.info = Data.itemById[item.id];

  let dropClass = Data.itemTypes[item.info.type].class;
  if (!dropClass && item.info.set) {
    dropClass = Data.itemSets[item.info.set].class;
  }

  function _stats(affixes, src) {
    const res = {};
    for (const id in affixes) {
      const stat = Data.stats[id];
      if (stat && dropClass && stat.classes && stat.classes.indexOf(dropClass) < 0) continue;
      if (src && src[id]) {
        res[id] = src[id];
      } else if (id === "sockets") {
        res[id] = [affixes[id].max];
      } else {
        res[id] = defaultValue({args: Data.stats[id]?.args, ...affixes[id]}, null, options.charClass, options.perfection);
      }
    }
    return res;
  }

  const counts = Data.getStatCount(item.id);
  item.affixes = {
    primary: counts[0],
    secondary: counts[1],
    all: _stats(Data.getItemAffixesById(item.id, item.ancient, false)),
  };

  item.state = {
    primary: 0,
    secondary: 0,
    groups: {},
  };

  const preset = Data.getItemPreset(item.id).map(stat => Data.smartListStats(stat, item.affixes.all, dropClass));
  item.affixes.preset = preset;

  const required = Data.getItemAffixesById(item.id, item.ancient, "only");
  var srcstats = item.stats;
  item.stats = _stats(required, srcstats);
  for (const id in required) {
    const stat = Data.stats[id];
    if (!stat) continue;
    if (stat.secondary) {
      item.state.secondary += 1;
    } else if (!stat.base) {
      item.state.primary += 1;
    }
    if (!required[id].noblock) {
      item.state.groups[stat.group || id] = true;
    }
  }

  if (options.unlock) {
    delete item.enchant;
    delete item.imported;
  }

  if (item.affixes.all.sockets) {
    item.gems = [];
    const slot = Data.itemTypes[item.info.type].slot;
    if (slot === "onehand" || slot === "twohand") {
      let gift = options.useGift;
      if (!gift && item.imported && srcstats.sockets) {
        let primary = 0;
        for (const id in srcstats) {
          if (Data.stats[id] && !Data.stats[id].secondary && !Data.stats[id].caldesanns && !Data.stats[id].base) {
            primary += 1;
          }
        }
        if (primary > item.affixes.primary) {
          gift = true;
        }
      }
      if (gift) {
        item.stats.sockets = item.affixes.all.sockets;
        item.state.groups.sockets = true;
      }
    }
  }

  if (item.imported) {
    if (item.enchant) {
      item.affixes.preset = [[item.enchant]];
    } else {
      item.affixes.preset = [];
    }
  }
  for (const id in srcstats) {
    const stat = Data.stats[id];
    if (!stat) continue;
    if (stat.caldesanns) {
      item.stats[id] = srcstats[id];
    } else if (item.imported && !stat.base && !required[id] && (id !== "sockets" || !item.stats.sockets)) {
      if (item.enchant && item.enchant !== id) {
        itemAddStat(item, id, srcstats[id]);
      } else if (!item.enchant) {
        item.affixes.preset.push([id]);
      }
    }
  }

  return item;
}

function itemUpdate(item, data) {
  item = {...item};
  item.stats = {};
  Object.keys(data.stats)
        .filter(id => Data.stats[id])
        .sort((a, b) => Data.stats[a].prio - Data.stats[b].prio)
        .forEach(id => item.stats[id] = data.stats[id]);
  if (item.gems && item.stats.sockets && item.stats.sockets[0]) {
    if (!data.gems) data.gems = [];
    for (let i = 0; i < item.gems.length && data.gems.length < item.stats.sockets[0]; ++i) {
      if (item.gems[i] && Data.legendaryGems[item.gems[i][0]]) {
        data.gems.push(item.gems[i]);
      }
    }
  }
  item.gems = data.gems;
  if (item.stats.sockets && item.stats.sockets[0]) {
    const count = item.stats.sockets[0];
    if (!item.gems) {
      item.gems = [...Array(count)];
    } else if (item.gems.length > count) {
      item.gems = item.gems.slice(0, count);
    } else if (item.gems.length < count) {
      item.gems = [...item.gems, ...Array(count - item.gems.length)];
    }
  }
  item.imported = data.imported;
  if (item.imported) {
    const imp = item.imported.stats;
    item.enchant = Object.keys(item.stats).find(id => {
      if (!data.affixes.all[id]) return false;
      if (Data.stats[id] && Data.stats[id].caldesanns) return false;
      if (!imp[id]) return true;
      if (imp[id].length >= 1 && item.stats[id][0] !== imp[id][0]) return true;
      if (imp[id].length >= 2 && item.stats[id][1] !== imp[id][1]) return true;
      return false;
    });
    if (!item.enchant) {
      item.enchant = item.imported.enchant;
    }
  } else {
    delete item.enchant;
  }
  return item;
}

function itemClone(item) {
  item = {...item};
  item.stats = clone(item.stats, true);
  item.state = clone(item.state, true);
  if (item.gems) item.gems = clone(item.gems, true);
  return item;
}

function itemAddPreset(item, charClass) {
  item = itemClone(item);
  var missing = 0;
  for (var i = 0; i < item.affixes.preset.length; ++i) {
    const list = item.affixes.preset[i];
    if (!list.length) continue;
    const has = list.some(id => {
      if (!item.stats[id]) return false;
      const imp = item.imported?.stats?.[id];
      return !imp || item.stats.every((val, i) => val === imp[i]);
    });
    if (!has) {
      const best = list.find(id => !Data.stats[id].classes || Data.stats[id].classes.includes(charClass)) || list[0];
      if (!itemAddStat(item, best, item.imported?.stats?.[best])) {
        missing += 1;
        if (missing > 1) {
          return false;
        }
      }
    }
  }
  return item;
}

export const compositeStats = {
  dps: {
    follower: false,
    name: () => _L`Damage`,
    options: {
      speed: {type: "checkbox", name: () => _L`Attack speed`},
      elem: {type: "select", options(state) {
        const charClass = state.profiles[state.activeProfile].class;
        const opt = [{id: "", name: _L`No Element`}];
        for (const id of ["phy", "fir", "col", "psn", "arc", "lit", "hol"]) {
          if (!Data.stats["dmg" + id].classes || Data.stats["dmg" + id].classes.includes(charClass)) {
            opt.push({id: "dmg" + id, name: Data.elements[id]});
          }
        }
        return opt;
      }},
      skill: {type: "select", options(state) {
        const charClass = state.profiles[state.activeProfile].class;
        const opt = [{id: "", name: _L`No Skill`}];
        const stats = Data.extendStats([], "skill_" + charClass);
        for (const id in Data.skills[charClass]) {
          if (stats.includes(`skill_${charClass}_${id}`)) {
            opt.push({id: `skill_${charClass}_${id}`, name: Data.skills[charClass][id].name});
          }
        }
        return opt;
      }},
      elite: {type: "checkbox", name: () => _L`vs Elites`},
    },
    stats(options, item, state) {
      const charClass = state.profiles[state.activeProfile].class;
      const list = ["wpnphy", "damage", "chc", "chd", Data.classes[charClass].primary];
      if (options.elem) list.push(options.elem);
      if (options.skill) list.push(options.skill);
      if (item) {
        for (const preset of item.affixes.preset) {
          const id = preset.find(id => Data.stats[id] && Data.stats[id].damage);
          if (id) {
            list[0] = id;
            break;
          }
        }
      }
      const slot = item && Data.itemTypes[item.info.type].slot;
      if (options.speed) list.push(slot === "onehand" || slot === "twohand" ? "weaponias" : "ias");
      if (options.elite) list.push("edmg");
      return list;
    },
    value(options, stats) {
      let res = (options.speed ? stats.info.dps : stats.info.dph);
      if (options.elem) res *= 1 + 0.01 * (stats[options.elem] || 0);
      if (options.elite) res *= 1 + 0.01 * (stats.edmg || 0);
      if (options.skill) res *= (1 + 0.01 * ((stats.damage || 0) + (stats[options.skill] || 0))) / (1 + 0.01 * (stats.damage || 0));
      return res;
    },
  },
  dpsFollower: {
    follower: true,
    name: () => _L`Damage`,
    options: {
      speed: {type: "checkbox", name: () => _L`Attack speed`},
      elite: {type: "checkbox", name: () => _L`vs Elites`},
    },
    stats(options, item, state) {
      const charClass = state.profiles[state.activeProfile].follower;
      const list = ["wpnphy", "damage", "chc", "chd", Data.classes[charClass].primary];
      if (item) {
        for (const preset of item.affixes.preset) {
          const id = preset.find(id => Data.stats[id] && Data.stats[id].damage);
          if (id) {
            list[0] = id;
            break;
          }
        }
      }
      const slot = item && Data.itemTypes[item.info.type].slot;
      if (options.speed) list.push(slot === "onehand" || slot === "twohand" ? "weaponias" : "ias");
      if (options.elite) list.push("edmg");
      return list;
    },
    value(options, stats) {
      let res = (options.speed ? stats.info.dps : stats.info.dph);
      if (options.elite) res *= 1 + 0.01 * (stats.edmg || 0);
      return res;
    },
  },
  toughness: {
    name: () => _L`Toughness`,
    options: {},
    stats() {
      return ["vit", "life", "resall", "armor", "str", "dex", "int", "resphy", "resfir", "rescol", "respsn", "resarc", "reslit", "edef", "meleedef", "rangedef"];
    },
    value(options, stats) {
      return stats.info.toughnessmin;
    },
  },
  healing: {
    name: () => _L`Healing`,
    options: {},
    stats(options, item) {
      const slot = item && Data.itemTypes[item.info.type].slot;
      const list = ["regen", "lph", "laek", "healbonus"];
      list.push(slot === "onehand" || slot === "twohand" ? "weaponias" : "ias");
      return list;
    },
    value(options, stats) {
      return stats.info.recoverymin;
    },
  },
};

export const skillCompositeId = (charClass, skill) => `skillinfo_${charClass}_${skill}`;
export function skillCompositeStat(charClass, skill) {
  const info = Data.skills[charClass][skill];
  function calculator(state, stats) {
    return (stats ? new InfoCalculator(state, stats) : getSkillInfo(state)).skill(skill);
  }
  function process(state, stats) {
    const calc = calculator(state, stats);
    const values = calc.values();
    const ret = {};
    if (!values) return ret;
    for (const id in values) {
      if (calc.source[id] && (typeof calc.source[id] !== "object" || calc.source[id].cooldown != null ||
          calc.source[id].duration != null || calc.source[id].cost != null || calc.source[id].value != null)) continue;
      if (values[id].value != null) ret[id] = values[id].value;
    }
    return ret;
  }
  return {
    follower: false,
    name: () => info.name,
    options: {
      value: {type: "select", options(state) {
        const results = process(state);
        return Object.keys(results).map(id => ({id, name: _L(id)}));
      }},
    },
    stats(options, item, state) {
      if (!item) return [];
      const stats = getStats(state);
      const values = calculator(state).source;
      const result = new Set(compositeStats.dps.stats({speed: true, elite: true}, item, state));
      function _addline(line) {
        if (!line || typeof line !== "object") return;
        if (line.sum) {
          for (const id in line) _addline(values[id]);
        } else {
          result.add(`skill_${charClass}_${(line && line.skill) || skill}`);
          if (line && line.elem) {
            if (line.elem === "max") {
              result.add("dmg" + (stats.info.maxelem || "fir"));
            } else {
              result.add("dmg" + line.elem);
            }
          }
        }
      }
      if (values) _addline(values[options.value]);
      result.add("cdr").add("rcr");
      return [...result];
    },
    value(options, stats, state) {
      const results = process(state, stats);
      return (results[options.value] || 0);
    },
  };
}

function finishStats(stats, state) {
  const profile = state.profiles[state.activeProfile];
  stats.finishLoad(profile);
  stats.applyGems(state);
  stats.loadSkills(state);
  stats.finalize();
  return stats;
}

function optimizeItemComplex(state, follower, items, slot, comp, options, simple) {
  let item = items[slot];

  const profile = state.profiles[state.activeProfile];

  const baseStats = (follower ? new FollowerStats(profile.follower) : new Stats(profile.class));
  baseStats.startLoad(profile);
  for (var id in items) {
    if (id !== slot) baseStats.addItem(id, items[id]);
  }
  function evaluate(item) {
    const tmpStats = clone(baseStats, true);
    tmpStats.addItem(slot, item);
    finishStats(tmpStats, state);
    return comp.value(options, tmpStats, state);
  }

  if (simple) {
    for (const stat of comp.stats(options, item, state)) {
      const next = itemClone(item);
      if (itemAddStat(next, stat)) {
        item = next;
      }
    }
    items[slot] = item;
  } else {
    const stats = comp.stats(options, item, state).filter(id => itemAddStat(itemClone(item), id));

    const cache = {"0": item};
    let bestValue = 0, bestItem = item;
    function tryStat(item, stat, value) {
      item = itemClone(item);
      if (itemAddStat(item, stat, value) && itemAddPreset(item, follower ? profile.follower : profile.class)) {
        const score = evaluate(item);
        if (score > bestValue) {
          bestValue = score;
          bestItem = item;
        }
        return item;
      }
    }

    for (let mask = 1; mask < (1 << stats.length); ++mask) {
      let statIndex = -1;
      if (item.imported && !item.enchant) {
        for (let i = 0; i < stats.length; ++i) {
          if ((mask & (1 << i)) && !item.imported.stats[stats[i]]) {
            statIndex = i;
          }
        }
      }
      if (statIndex < 0) {
        statIndex = 0;
        while (!(mask & (1 << statIndex))) {
          ++statIndex;
        }
      }
      const prev = mask - (1 << statIndex);
      if (!cache[prev]) continue;

      if (item.imported && !item.enchant && item.imported.stats[stats[statIndex]]) {
        cache[mask] = tryStat(cache[prev], stats[statIndex], item.imported.stats[stats[statIndex]]);

        for (let i = 0; i < stats.length; ++i) {
          if (!(mask & (1 << i))) continue;
          const src = mask - (1 << i);
          if (!cache[src]) continue;
          tryStat(cache[src], stats[i]);
        }
      } else {
        const curItem = tryStat(cache[prev], stats[statIndex]);
        if (!item.imported || item.enchant) {
          cache[mask] = curItem;
        }
      }
    }

    item = items[slot] = bestItem;
  }

  const totalGems = item.stats.sockets ? item.stats.sockets[0] : 0;
  const fittedGems = item.gems ? item.gems.filter(Boolean).length : 0;
  if (fittedGems < totalGems) {
    const slotType = Data.itemTypes[item.info.type].slot;
    const type = (slotType && (Data.itemSlots[slotType] || Data.metaSlots[slotType])) || {};
    if (type.socketType !== "jewelry") {
      const sockType = (type.socketType === "weapon" || type.socketType === "head" ? type.socketType : "other");
      let bestColor = null, bestValue = evaluate(item);
      const oldGems = item.gems.slice();
      for (const clr in Data.gemColors) {
        for (let i = 0; i < totalGems; ++i) {
          if (!item.gems[i]) {
            item.gems[i] = [Data.gemColors[clr][sockType].amount.length - 1, clr];
          }
        }
        const value = evaluate(item);
        item.gems = oldGems.slice();
        if (value > bestValue) {
          bestValue = value;
          bestColor = clr;
        }
      }
      if (bestColor) {
        for (let i = 0; i < totalGems; ++i) {
          if (!item.gems[i]) {
            item.gems[i] = [Data.gemColors[bestColor][sockType].amount.length - 1, bestColor];
          }
        }
      }
    }
  }

  return item;
}

function optimizeItem(state, follower, items, slot, stat) {
  const profile = state.profiles[state.activeProfile];

  let item = items[slot];
  const tmpItem = itemClone(items[slot]);
  if (itemAddStat(tmpItem, stat) && itemAddPreset(tmpItem, follower ? profile.follower : profile.class)) {
    item = items[slot] = tmpItem;
  }

  const totalGems = item.stats.sockets ? item.stats.sockets[0] : 0;
  const fittedGems = item.gems ? item.gems.filter(Boolean).length : 0;
  if (fittedGems < totalGems) {
    const slotType = Data.itemTypes[item.info.type].slot;
    const type = (slotType && (Data.itemSlots[slotType] || Data.metaSlots[slotType])) || {};
    if (type.socketType !== "jewelry") {
      const sockType = (type.socketType === "weapon" || type.socketType === "head" ? type.socketType : "other");
      const color = Object.keys(Data.gemColors).find(clr => Data.gemColors[clr][sockType].stat === stat);
      if (color) {
        for (let i = 0; i < totalGems; ++i) {
          if (!item.gems[i]) {
            item.gems[i] = [Data.gemColors[color][sockType].amount.length - 1, color];
          }
        }
      }
    }
  }

  return item;
}

export function optimizeItems(state, follower, options) {
  const profile = state.profiles[state.activeProfile];

  const items = {};
  const profileItems = (follower ? profile.followerItems : profile.items);
  options = {...options, charClass: follower ? profile.follower : profile.class};
  for (const slot in profileItems) {
    if (profileItems[slot]) {
      items[slot] = itemCreate(profileItems[slot], options);
    }
  }

  const composite = {...compositeStats};
  if (!follower) {
    for (const skill of profile.skills) {
      if (skill) {
        const comp = skillCompositeStat(profile.class, skill[0]);
        composite[skillCompositeId(profile.class, skill[0])] = comp;
      }
    }
  }

  for (const prio of profile.statPriority) {
    if (composite[prio.stat] && (composite[prio.stat].follower == null || composite[prio.stat].follower === follower)) {
      const backup = {...items};
      for (let iter = 0; iter < 2; ++iter) {
        for (const slot in items) {
          items[slot] = backup[slot];
          optimizeItemComplex(state, follower, items, slot, composite[prio.stat], prio.options, iter === 0);
        }
      }
    } else if (Data.stats[prio.stat]) {
      for (const slot in items) {
        if (prio.stat === "sockets" && Data.itemTypes[items[slot].info.type].slot === "offhand") continue;
        optimizeItem(state, follower, items, slot, prio.stat);
      }
    }
  }
  for (const slot in items) {
    items[slot] = itemAddPreset(items[slot], options.charClass) || items[slot];
  }

  const result = {...profileItems};
  for (const slot in items) {
    result[slot] = itemUpdate(result[slot], items[slot]);
  }
  return result;
}
